import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import {
  conversationApi,
  messageApi,
  receiptApi,
  pickerApi,
  memberSummaryApi,
  uploadApi,
  typingApi,
  pinApi,
  muteApi,
  favoriteApi,
} from '@formosa/shared/api/im';
import imWebSocketClient from '@formosa/shared/api/imWebSocketClient';
import { getMemberId } from '@formosa/shared/auth/tokenStorage';
import { useAuth } from '../auth/AuthContext';
import {
  chatReducer,
  createClientMessageId,
  initialChatState,
} from './chatReducer';
import { isCustomerServiceConversation } from './socialImConversations';
import { messageMentionsMember, normalizeMuteMode } from './muteHelpers';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { isAuthenticated, token } = useAuth();
  const [state, dispatch] = useReducer(chatReducer, initialChatState);
  const myMemberId = getMemberId();
  const stateRef = useRef(state);
  stateRef.current = state;
  const readTimers = useRef({});

  const refreshSummaries = useCallback(async (memberIds) => {
    const missing = [...new Set(memberIds || [])].filter(
      (id) => id != null && !stateRef.current.participantSummaries[id]
    );
    if (!missing.length) return;
    try {
      const rows = await memberSummaryApi.batch(missing);
      const summaries = {};
      (rows || []).forEach((row) => {
        if (row?.memberId != null) {
          summaries[row.memberId] = row;
        }
      });
      dispatch({ type: 'SUMMARIES_MERGED', summaries });
    } catch {
      /* ignore summary errors */
    }
  }, []);

  const loadConversations = useCallback(async () => {
    dispatch({ type: 'CONVERSATIONS_LOADING' });
    try {
      const list = await conversationApi.listMine();
      dispatch({ type: 'CONVERSATIONS_LOADED', conversations: list || [] });
      const ids = [];
      (list || []).forEach((c) => {
        if (c.peerMemberId) ids.push(c.peerMemberId);
        (c.participantMemberIds || []).forEach((id) => ids.push(id));
      });
      refreshSummaries(ids);
    } catch (err) {
      dispatch({ type: 'CONVERSATIONS_ERROR', error: err?.message || '載入會話失敗' });
    }
  }, [refreshSummaries]);

  const loadLatestMessages = useCallback(async (conversationId) => {
    if (conversationId == null) return;
    dispatch({ type: 'TIMELINE_LOADING', conversationId, more: false });
    try {
      const page = await messageApi.list(conversationId, { limit: 50 });
      dispatch({
        type: 'TIMELINE_SET',
        conversationId,
        items: page?.items || [],
        hasMoreBefore: !!page?.hasMoreBefore,
        oldestSeq: page?.oldestSeq,
        newestSeq: page?.newestSeq,
      });
    } catch (err) {
      dispatch({
        type: 'TIMELINE_ERROR',
        conversationId,
        error: err?.message || '載入訊息失敗',
      });
    }
  }, []);

  const loadOlderMessages = useCallback(async (conversationId) => {
    const timeline = stateRef.current.messagesByConversation[String(conversationId)];
    if (!timeline?.hasMoreBefore || timeline.loadingMore || timeline.oldestSeq == null) {
      return;
    }
    dispatch({ type: 'TIMELINE_LOADING', conversationId, more: true });
    try {
      const page = await messageApi.list(conversationId, {
        beforeSeq: timeline.oldestSeq,
        limit: 50,
      });
      dispatch({
        type: 'TIMELINE_PREPEND',
        conversationId,
        items: page?.items || [],
        hasMoreBefore: !!page?.hasMoreBefore,
        oldestSeq: page?.oldestSeq,
        newestSeq: page?.newestSeq,
      });
    } catch (err) {
      dispatch({
        type: 'TIMELINE_ERROR',
        conversationId,
        error: err?.message || '載入舊訊失敗',
      });
    }
  }, []);

  /** 搜尋命中後跳轉：載入含目標 seq 的一頁並高亮。 */
  const jumpToMessage = useCallback(async (conversationId, message) => {
    if (conversationId == null || !message?.seq) return;
    const seq = Number(message.seq);
    const timeline = stateRef.current.messagesByConversation[String(conversationId)];
    const already = (timeline?.items || []).some(
      (m) => m.serverMessageId && m.serverMessageId === message.serverMessageId
    );
    if (!already) {
      dispatch({ type: 'TIMELINE_LOADING', conversationId, more: false });
      try {
        const page = await messageApi.list(conversationId, {
          beforeSeq: seq + 1,
          limit: 50,
        });
        dispatch({
          type: 'TIMELINE_SET',
          conversationId,
          items: page?.items || [],
          hasMoreBefore: !!page?.hasMoreBefore,
          oldestSeq: page?.oldestSeq,
          newestSeq: page?.newestSeq,
        });
      } catch (err) {
        dispatch({
          type: 'TIMELINE_ERROR',
          conversationId,
          error: err?.message || '無法定位訊息',
        });
        throw err;
      }
    }
    dispatch({
      type: 'HIGHLIGHT_MESSAGE',
      conversationId,
      serverMessageId: message.serverMessageId || null,
    });
    setTimeout(() => {
      dispatch({ type: 'HIGHLIGHT_MESSAGE', conversationId, serverMessageId: null });
    }, 3500);
  }, []);

  const gapFillAfterSeq = useCallback(async (conversationId, afterSeq) => {
    if (conversationId == null || afterSeq == null) return;
    try {
      const page = await messageApi.list(conversationId, { afterSeq, limit: 100 });
      if (page?.items?.length) {
        dispatch({ type: 'TIMELINE_APPEND', conversationId, items: page.items });
      }
    } catch {
      /* ignore gap fill errors */
    }
  }, []);

  const loadReadStates = useCallback(async (conversationId) => {
    try {
      const data = await receiptApi.listStates(conversationId);
      dispatch({
        type: 'READ_STATES_SET',
        conversationId,
        lastMessageSeq: data?.lastMessageSeq,
        participants: data?.participants || [],
      });
    } catch {
      /* ignore */
    }
  }, []);

  const loadPins = useCallback(async (conversationId) => {
    if (conversationId == null) return;
    try {
      const pins = await pinApi.list(conversationId);
      dispatch({ type: 'PINS_SET', conversationId, pins: pins || [] });
    } catch {
      dispatch({ type: 'PINS_SET', conversationId, pins: [] });
    }
  }, []);

  const pinMessage = useCallback(async (conversationId, serverMessageId) => {
    const pins = await pinApi.pin(conversationId, serverMessageId);
    dispatch({ type: 'PINS_SET', conversationId, pins: pins || [] });
    return pins;
  }, []);

  const unpinMessage = useCallback(async (conversationId, serverMessageId) => {
    const pins = await pinApi.unpin(conversationId, serverMessageId);
    dispatch({ type: 'PINS_SET', conversationId, pins: pins || [] });
    return pins;
  }, []);

  const setConversationMuted = useCallback(async (conversationId, muteModeOrMuted = 'ALL') => {
    const res = await muteApi.set(conversationId, muteModeOrMuted);
    const muteMode = res?.muteMode || (res?.muted ? 'ALL' : 'OFF');
    dispatch({
      type: 'UPSERT_CONVERSATION',
      conversation: {
        conversationId,
        id: conversationId,
        muteMode,
        muted: muteMode !== 'OFF',
      },
    });
    return res;
  }, []);

  const loadFavoriteIds = useCallback(async (conversationId) => {
    if (conversationId == null) return;
    try {
      const ids = await favoriteApi.listIds(conversationId);
      dispatch({
        type: 'FAVORITES_SET',
        conversationId,
        serverMessageIds: ids || [],
      });
    } catch {
      dispatch({ type: 'FAVORITES_SET', conversationId, serverMessageIds: [] });
    }
  }, []);

  const toggleFavorite = useCallback(async (conversationId, serverMessageId, favorited) => {
    const ids = favorited
      ? await favoriteApi.remove(conversationId, serverMessageId)
      : await favoriteApi.add(conversationId, serverMessageId);
    dispatch({
      type: 'FAVORITES_SET',
      conversationId,
      serverMessageIds: ids || [],
    });
    return ids;
  }, []);

  const scheduleMarkRead = useCallback((conversationId, readSeq) => {
    if (conversationId == null || readSeq == null) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    const id = String(conversationId);
    const conv = stateRef.current.conversations.find(
      (c) => String(c.conversationId || c.id) === id
    );
    const current = conv?.myLastReadSeq ?? 0;
    if (readSeq <= current) return;

    if (readTimers.current[id]) {
      clearTimeout(readTimers.current[id]);
    }
    readTimers.current[id] = setTimeout(async () => {
      try {
        const res = await receiptApi.markRead(conversationId, readSeq);
        dispatch({
          type: 'READ_UPDATED',
          conversationId,
          memberId: myMemberId,
          myMemberId,
          lastReadSeq: res?.lastReadSeq ?? readSeq,
          lastMessageSeq: res?.lastMessageSeq,
        });
        dispatch({
          type: 'UPSERT_CONVERSATION',
          conversation: {
            ...(conv || {}),
            conversationId,
            id: conversationId,
            myLastReadSeq: res?.lastReadSeq ?? readSeq,
            unreadCount: res?.unreadCount ?? 0,
          },
        });
      } catch {
        /* ignore */
      }
    }, 800);
  }, [myMemberId]);

  const openConversation = useCallback(
    async (conversationId) => {
      dispatch({ type: 'SET_ACTIVE_CONVERSATION', conversationId });
      const timeline = stateRef.current.messagesByConversation[String(conversationId)];
      if (!timeline?.initialized) {
        await loadLatestMessages(conversationId);
      }
      await loadReadStates(conversationId);
      await loadPins(conversationId);
      await loadFavoriteIds(conversationId);
      try {
        const detail = await conversationApi.getById(conversationId);
        dispatch({ type: 'UPSERT_CONVERSATION', conversation: detail });
        const ids = [...(detail?.participantMemberIds || [])];
        if (detail?.peerMemberId) ids.push(detail.peerMemberId);
        refreshSummaries(ids);
      } catch {
        /* ignore */
      }
    },
    [loadLatestMessages, loadReadStates, loadPins, loadFavoriteIds, refreshSummaries]
  );

  const sendOptimistic = useCallback(
    async (conversationId, buildLocal, sendFn) => {
      const clientMessageId = createClientMessageId();
      const replyTo = stateRef.current.replyTargets[String(conversationId)] || null;
      const local = {
        ...buildLocal(clientMessageId, replyTo),
        conversationId,
        clientMessageId,
        senderMemberId: myMemberId,
        createdAt: new Date().toISOString(),
        status: 'SENT',
        _localStatus: 'sending',
      };
      dispatch({ type: 'OPTIMISTIC_ADD', message: local });
      dispatch({ type: 'REPLY_SET', conversationId, replyTo: null });
      typingApi.signal(conversationId, false).catch(() => {});
      try {
        const body = { clientMessageId };
        if (replyTo?.serverMessageId) {
          body.replyToServerMessageId = replyTo.serverMessageId;
        }
        const saved = await sendFn(body, local);
        dispatch({ type: 'MESSAGE_UPSERT', message: { ...saved, _localStatus: 'sent' } });
        dispatch({
          type: 'UPSERT_CONVERSATION',
          conversation: {
            conversationId,
            id: conversationId,
            lastMessageSeq: saved.seq,
            lastMessageAt: saved.createdAt,
            lastMessageTime: saved.createdAt,
            lastMessageType: saved.messageType,
            lastMessagePreview: previewOf(saved),
            lastMessageSenderId: saved.senderMemberId,
          },
        });
        return saved;
      } catch (err) {
        dispatch({
          type: 'OPTIMISTIC_FAIL',
          conversationId,
          clientMessageId,
          error: err?.message || '發送失敗',
        });
        throw err;
      }
    },
    [myMemberId]
  );

  const sendText = useCallback(
    (conversationId, text, mentionMemberIds) =>
      sendOptimistic(
        conversationId,
        (clientMessageId, replyTo) => ({
          messageType: 'TEXT',
          text,
          replyTo,
          mentions: (mentionMemberIds || []).map((id) => ({ memberId: id })),
        }),
        (body) =>
          messageApi.sendText(conversationId, {
            ...body,
            text,
            ...(Array.isArray(mentionMemberIds) && mentionMemberIds.length
              ? { mentionMemberIds }
              : {}),
          })
      ),
    [sendOptimistic]
  );

  const sendImage = useCallback(
    async (conversationId, file, caption) => {
      const objectKey = await uploadApi.image(file);
      return sendOptimistic(
        conversationId,
        (clientMessageId, replyTo) => ({
          messageType: 'IMAGE',
          objectKey,
          text: caption || '',
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          replyTo,
        }),
        (body) =>
          messageApi.sendImage(conversationId, {
            ...body,
            objectKey,
            caption,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          })
      );
    },
    [sendOptimistic]
  );

  const sendSticker = useCallback(
    (conversationId, { stickerId, emoteCode }) =>
      sendOptimistic(
        conversationId,
        (_id, replyTo) => ({
          messageType: 'STICKER',
          stickerId,
          emoteCode,
          replyTo,
        }),
        (body) =>
          messageApi.sendSticker(conversationId, {
            ...body,
            stickerId,
            emoteCode,
          })
      ),
    [sendOptimistic]
  );

  const sendGif = useCallback(
    async (conversationId, payload) => {
      let objectKey = payload.objectKey;
      if (payload.file) {
        objectKey = await uploadApi.gif(payload.file);
      }
      return sendOptimistic(
        conversationId,
        (_id, replyTo) => ({
          messageType: 'GIF',
          objectKey,
          emoteCode: payload.emoteCode,
          gifId: payload.gifId,
          replyTo,
        }),
        (body) =>
          messageApi.sendGif(conversationId, {
            ...body,
            objectKey,
            emoteCode: payload.emoteCode,
            gifId: payload.gifId,
          })
      );
    },
    [sendOptimistic]
  );

  const sendAudio = useCallback(
    async (conversationId, file) => {
      const objectKey = await uploadApi.audio(file);
      return sendOptimistic(
        conversationId,
        (_id, replyTo) => ({
          messageType: 'AUDIO',
          objectKey,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          replyTo,
        }),
        (body) =>
          messageApi.sendAudio(conversationId, {
            ...body,
            objectKey,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          })
      );
    },
    [sendOptimistic]
  );

  const sendVideo = useCallback(
    async (conversationId, file) => {
      const objectKey = await uploadApi.video(file);
      return sendOptimistic(
        conversationId,
        (_id, replyTo) => ({
          messageType: 'VIDEO',
          objectKey,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          replyTo,
        }),
        (body) =>
          messageApi.sendVideo(conversationId, {
            ...body,
            objectKey,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          })
      );
    },
    [sendOptimistic]
  );

  const sendFile = useCallback(
    async (conversationId, file) => {
      const objectKey = await uploadApi.file(file);
      return sendOptimistic(
        conversationId,
        (_id, replyTo) => ({
          messageType: 'FILE',
          objectKey,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          replyTo,
        }),
        (body) =>
          messageApi.sendFile(conversationId, {
            ...body,
            objectKey,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          })
      );
    },
    [sendOptimistic]
  );

  const sendLocation = useCallback(
    (conversationId, { latitude, longitude, locationName, locationAddress, caption }) =>
      sendOptimistic(
        conversationId,
        (_id, replyTo) => ({
          messageType: 'LOCATION',
          latitude,
          longitude,
          locationName,
          locationAddress,
          text: caption || locationName || '[位置]',
          replyTo,
        }),
        (body) =>
          messageApi.sendLocation(conversationId, {
            ...body,
            latitude,
            longitude,
            locationName,
            locationAddress,
            caption,
          })
      ),
    [sendOptimistic]
  );

  const revokeMessage = useCallback(async (conversationId, serverMessageId) => {
    const saved = await messageApi.revoke(conversationId, serverMessageId);
    dispatch({ type: 'MESSAGE_UPSERT', message: saved });
    return saved;
  }, []);

  const editMessage = useCallback(async (conversationId, serverMessageId, text, mentionMemberIds) => {
    const saved = await messageApi.editText(
      conversationId,
      serverMessageId,
      text,
      mentionMemberIds
    );
    dispatch({ type: 'MESSAGE_UPSERT', message: saved });
    dispatch({ type: 'EDIT_SET', conversationId, editTarget: null });
    dispatch({ type: 'DRAFT_SET', conversationId, draft: '' });
    typingApi.signal(conversationId, false).catch(() => {});
    const conv = stateRef.current.conversations.find(
      (c) => String(c.conversationId || c.id) === String(conversationId)
    );
    const isLatest =
      conv?.lastMessageSeq != null &&
      saved?.seq != null &&
      Number(conv.lastMessageSeq) === Number(saved.seq);
    if (isLatest && saved?.conversationId) {
      dispatch({
        type: 'UPSERT_CONVERSATION',
        conversation: {
          conversationId: saved.conversationId,
          id: saved.conversationId,
          lastMessagePreview: previewOf(saved),
          lastMessageType: saved.messageType,
        },
      });
    }
    return saved;
  }, []);

  const reactToMessage = useCallback(async (conversationId, serverMessageId, emoji) => {
    const saved = await messageApi.putReaction(conversationId, serverMessageId, emoji);
    dispatch({ type: 'MESSAGE_UPSERT', message: saved });
    return saved;
  }, []);

  const typingTimers = useRef({});
  const notifyTyping = useCallback((conversationId, typing = true) => {
    if (conversationId == null) return;
    const cid = String(conversationId);
    if (!typing) {
      const t = typingTimers.current[cid];
      if (t?.idle) clearTimeout(t.idle);
      typingTimers.current[cid] = { lastSent: 0 };
      typingApi.signal(cid, false).catch(() => {});
      return;
    }
    const now = Date.now();
    const prev = typingTimers.current[cid] || { lastSent: 0 };
    if (prev.idle) clearTimeout(prev.idle);
    const idle = setTimeout(() => {
      typingApi.signal(cid, false).catch(() => {});
      typingTimers.current[cid] = { lastSent: 0 };
    }, 4000);
    if (now - (prev.lastSent || 0) < 2500) {
      typingTimers.current[cid] = { ...prev, idle };
      return;
    }
    typingTimers.current[cid] = { lastSent: now, idle };
    typingApi.signal(cid, true).catch(() => {});
  }, []);

  const retryFailed = useCallback(
    async (conversationId, clientMessageId) => {
      const failed = stateRef.current.failedMessages[String(conversationId)]?.[clientMessageId];
      if (!failed) return;
      dispatch({ type: 'FAILED_CLEAR', conversationId, clientMessageId });
      const type = failed.messageType;
      if (type === 'TEXT') {
        return sendText(conversationId, failed.text);
      }
      // Re-send with same clientMessageId via direct API for media is complex without file;
      // for TEXT we regenerate id in sendOptimistic — contract prefers same id.
      // Keep simple: re-add optimistic with SAME clientMessageId
      dispatch({
        type: 'OPTIMISTIC_ADD',
        message: { ...failed, _localStatus: 'sending', clientMessageId },
      });
      try {
        let saved;
        const base = {
          clientMessageId,
          replyToServerMessageId: failed.replyTo?.serverMessageId,
        };
        if (type === 'TEXT') {
          saved = await messageApi.sendText(conversationId, { ...base, text: failed.text });
        } else if (type === 'IMAGE' && failed.objectKey) {
          saved = await messageApi.sendImage(conversationId, {
            ...base,
            objectKey: failed.objectKey,
            caption: failed.text,
            fileName: failed.fileName,
            fileSize: failed.fileSize,
            mimeType: failed.mimeType,
          });
        } else if (type === 'STICKER') {
          saved = await messageApi.sendSticker(conversationId, {
            ...base,
            stickerId: failed.stickerId,
            emoteCode: failed.emoteCode,
          });
        } else if (type === 'GIF') {
          saved = await messageApi.sendGif(conversationId, {
            ...base,
            objectKey: failed.objectKey,
            emoteCode: failed.emoteCode,
            gifId: failed.gifId,
          });
        } else if (type === 'AUDIO' && failed.objectKey) {
          saved = await messageApi.sendAudio(conversationId, {
            ...base,
            objectKey: failed.objectKey,
            fileName: failed.fileName,
            fileSize: failed.fileSize,
            mimeType: failed.mimeType,
          });
        } else if (type === 'VIDEO' && failed.objectKey) {
          saved = await messageApi.sendVideo(conversationId, {
            ...base,
            objectKey: failed.objectKey,
            fileName: failed.fileName,
            fileSize: failed.fileSize,
            mimeType: failed.mimeType,
          });
        } else if (type === 'FILE' && failed.objectKey) {
          saved = await messageApi.sendFile(conversationId, {
            ...base,
            objectKey: failed.objectKey,
            fileName: failed.fileName,
            fileSize: failed.fileSize,
            mimeType: failed.mimeType,
          });
        } else if (type === 'LOCATION') {
          saved = await messageApi.sendLocation(conversationId, {
            ...base,
            latitude: failed.latitude,
            longitude: failed.longitude,
            locationName: failed.locationName,
            locationAddress: failed.locationAddress,
            caption: failed.text,
          });
        } else {
          throw new Error('無法重試此訊息類型');
        }
        dispatch({ type: 'MESSAGE_UPSERT', message: { ...saved, _localStatus: 'sent' } });
      } catch (err) {
        dispatch({
          type: 'OPTIMISTIC_FAIL',
          conversationId,
          clientMessageId,
          error: err?.message || '重試失敗',
        });
      }
    },
    [sendText]
  );

  const openPicker = useCallback(async (tab = 'stickers') => {
    dispatch({ type: 'PICKER_SET', patch: { open: true, tab, loading: true, data: null } });
    try {
      const data =
        tab === 'gifs' ? await pickerApi.gifs() : await pickerApi.stickers();
      dispatch({ type: 'PICKER_SET', patch: { loading: false, data } });
    } catch (err) {
      dispatch({
        type: 'PICKER_SET',
        patch: { loading: false, data: null, error: err?.message },
      });
    }
  }, []);

  const searchPicker = useCallback(async (tab, q) => {
    dispatch({ type: 'PICKER_SET', patch: { query: q, loading: true } });
    try {
      const data = tab === 'gifs' ? await pickerApi.gifs(q) : await pickerApi.stickers(q);
      dispatch({ type: 'PICKER_SET', patch: { loading: false, data } });
    } catch {
      dispatch({ type: 'PICKER_SET', patch: { loading: false } });
    }
  }, []);

  // Auth → WS lifecycle
  useEffect(() => {
    if (!isAuthenticated || !token) {
      imWebSocketClient.disable();
      dispatch({ type: 'SOCKET_STATUS', status: 'idle' });
      return undefined;
    }
    imWebSocketClient.reconnectWithToken();
    const offStatus = imWebSocketClient.onStatus((status) => {
      dispatch({ type: 'SOCKET_STATUS', status });
      if (status === 'open') {
        const active = stateRef.current.activeConversationId;
        if (active != null) {
          const tl = stateRef.current.messagesByConversation[String(active)];
          if (tl?.newestSeq != null) {
            gapFillAfterSeq(active, tl.newestSeq);
          }
        }
      }
    });
    const offMsg = imWebSocketClient.subscribe((envelope) => {
      handleRealtime(envelope, dispatch, myMemberId, stateRef, gapFillAfterSeq, refreshSummaries);
    });
    loadConversations();
    return () => {
      offStatus();
      offMsg();
      imWebSocketClient.disable();
    };
  }, [isAuthenticated, token, gapFillAfterSeq, loadConversations, myMemberId, refreshSummaries]);

  const value = useMemo(
    () => ({
      state,
      dispatch,
      myMemberId,
      loadConversations,
      openConversation,
      loadLatestMessages,
      loadOlderMessages,
      jumpToMessage,
      scheduleMarkRead,
      sendText,
      sendImage,
      sendSticker,
      sendGif,
      sendAudio,
      sendVideo,
      sendFile,
      sendLocation,
      revokeMessage,
      editMessage,
      reactToMessage,
      notifyTyping,
      pinMessage,
      unpinMessage,
      setConversationMuted,
      toggleFavorite,
      retryFailed,
      openPicker,
      searchPicker,
      refreshSummaries,
    }),
    [
      state,
      myMemberId,
      loadConversations,
      openConversation,
      loadLatestMessages,
      loadOlderMessages,
      jumpToMessage,
      scheduleMarkRead,
      sendText,
      sendImage,
      sendSticker,
      sendGif,
      sendAudio,
      sendVideo,
      sendFile,
      sendLocation,
      revokeMessage,
      editMessage,
      reactToMessage,
      notifyTyping,
      pinMessage,
      unpinMessage,
      setConversationMuted,
      toggleFavorite,
      retryFailed,
      openPicker,
      searchPicker,
      refreshSummaries,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return ctx;
}

function previewOf(msg) {
  if (!msg) return '';
  if (msg.status === 'REVOKED') return '[已撤回]';
  switch (msg.messageType) {
    case 'TEXT':
      return msg.text || '';
    case 'IMAGE':
      return msg.text || '[圖片]';
    case 'STICKER':
      return msg.emoteCode || '[貼圖]';
    case 'GIF':
      return msg.emoteCode || '[GIF]';
    case 'AUDIO':
      return '[語音]';
    case 'VIDEO':
      return '[影片]';
    case 'FILE':
      return msg.fileName || '[檔案]';
    case 'LOCATION':
      return msg.locationName || '[位置]';
    case 'MAP_POINT':
      return msg.mapPoint?.title || '[地圖點]';
    case 'ITINERARY':
      return msg.itinerary?.title || '[行程]';
    case 'GROUP_TRIP':
      return msg.groupTrip?.title || '[揪團]';
    case 'SOCIAL_POST':
      return msg.socialPost?.title || '[貼文]';
    default:
      return msg.text || '[訊息]';
  }
}

function handleRealtime(envelope, dispatch, myMemberId, stateRef, _gapFillAfterSeq, refreshSummaries) {
  const { type, data } = envelope || {};
  if (!type) return;

  if (type === 'MESSAGE_CREATED' || type === 'MESSAGE_REVOKED' || type === 'MESSAGE_EDITED') {
    const msg = data;
    if (!msg?.conversationId) return;
    dispatch({ type: 'MESSAGE_UPSERT', message: msg });
    if (msg.senderMemberId != null) {
      dispatch({
        type: 'TYPING_CLEAR_MEMBER',
        conversationId: msg.conversationId,
        memberId: msg.senderMemberId,
      });
    }
    // 客服通道不進社交 IM 列表（含未來揪團客服 binding）
    if (isCustomerServiceConversation(msg.conversationType)) {
      return;
    }
    if (type === 'MESSAGE_EDITED') {
      const conv = stateRef?.current?.conversations?.find(
        (c) => String(c.conversationId || c.id) === String(msg.conversationId)
      );
      const isLatest =
        conv?.lastMessageSeq != null && msg.seq != null && Number(conv.lastMessageSeq) === Number(msg.seq);
      if (isLatest) {
        dispatch({
          type: 'UPSERT_CONVERSATION',
          conversation: {
            conversationId: msg.conversationId,
            id: msg.conversationId,
            lastMessageType: msg.messageType,
            lastMessagePreview: previewOf(msg),
          },
        });
      }
      return;
    }
    const existing = stateRef?.current?.conversations?.find(
      (c) => String(c.conversationId || c.id) === String(msg.conversationId)
    );
    const myLastRead = Number(existing?.myLastReadSeq) || 0;
    const seq = msg.seq != null ? Number(msg.seq) : null;
    const patch = {
      conversationId: msg.conversationId,
      id: msg.conversationId,
      lastMessageSeq: msg.seq,
      lastMessageAt: msg.createdAt,
      lastMessageTime: msg.createdAt,
      lastMessageType: msg.messageType,
      lastMessagePreview: previewOf(msg),
      lastMessageSenderId: msg.senderMemberId,
    };
    if (seq != null && String(msg.senderMemberId) !== String(myMemberId)) {
      patch.unreadCount = Math.max(0, seq - myLastRead);
    }
    if (
      type === 'MESSAGE_CREATED' &&
      messageMentionsMember(msg, myMemberId) &&
      normalizeMuteMode(existing) === 'EXCEPT_MENTION'
    ) {
      patch.hasUnreadMention = true;
    }
    dispatch({
      type: 'UPSERT_CONVERSATION',
      conversation: patch,
    });
    if (msg.senderMemberId) {
      refreshSummaries([msg.senderMemberId]);
    }
    return;
  }

  if (type === 'MESSAGE_REACTION_UPDATED') {
    const payload = data;
    if (!payload?.conversationId || !payload?.serverMessageId) return;
    const reactions = (payload.reactions || []).map((b) => ({
      emoji: b.emoji,
      count: b.count,
      reactedByMe: (b.memberIds || []).some((id) => String(id) === String(myMemberId)),
    }));
    dispatch({
      type: 'MESSAGE_REACTIONS_PATCH',
      conversationId: payload.conversationId,
      serverMessageId: payload.serverMessageId,
      reactions,
    });
    return;
  }

  if (type === 'TYPING_UPDATED') {
    const payload = data;
    if (!payload?.conversationId || payload.memberId == null) return;
    if (String(payload.memberId) === String(myMemberId)) return;
    const expiresAtMs = payload.expiresAt ? new Date(payload.expiresAt).getTime() : Date.now() + 5000;
    dispatch({
      type: 'TYPING_SET',
      conversationId: payload.conversationId,
      memberId: payload.memberId,
      typing: payload.typing !== false,
      expiresAtMs,
    });
    return;
  }

  if (type === 'PINS_UPDATED') {
    const payload = data;
    if (!payload?.conversationId) return;
    dispatch({
      type: 'PINS_SET',
      conversationId: payload.conversationId,
      pins: payload.pins || [],
    });
    return;
  }

  if (type === 'MUTE_UPDATED') {
    const payload = data;
    if (!payload?.conversationId) return;
    if (payload.memberId != null && String(payload.memberId) !== String(myMemberId)) return;
    const muteMode = payload.muteMode || (payload.muted ? 'ALL' : 'OFF');
    dispatch({
      type: 'UPSERT_CONVERSATION',
      conversation: {
        conversationId: payload.conversationId,
        id: payload.conversationId,
        muteMode,
        muted: muteMode !== 'OFF',
      },
    });
    return;
  }

  if (type === 'FAVORITES_UPDATED') {
    const payload = data;
    if (!payload?.conversationId) return;
    if (payload.memberId != null && String(payload.memberId) !== String(myMemberId)) return;
    dispatch({
      type: 'FAVORITES_SET',
      conversationId: payload.conversationId,
      serverMessageIds: payload.serverMessageIds || [],
    });
    return;
  }

  if (type === 'READ_UPDATED') {
    dispatch({
      type: 'READ_UPDATED',
      conversationId: data.conversationId,
      memberId: data.memberId,
      myMemberId,
      lastReadSeq: data.lastReadSeq,
      lastMessageSeq: data.lastMessageSeq,
    });
    return;
  }

  if (
    type === 'GROUP_UPDATED' ||
    type === 'GROUP_CREATED' ||
    type === 'MEMBER_JOINED' ||
    type === 'MEMBER_LEFT' ||
    type === 'MEMBER_REMOVED'
  ) {
    // Soft refresh list
    conversationApi.listMine().then((list) => {
      dispatch({ type: 'CONVERSATIONS_LOADED', conversations: list || [] });
    }).catch(() => {});
  }
}
